import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { isAuthenticated } from '../services/authService';
import { AdaptiveCard } from 'adaptivecards-react';
import Header from './Header';
import './EventDetails.css';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { useTheme } from '@mui/material/styles'; // Import useTheme
import EventDetailsCard from './EventDetailsCard';
import { useTenant } from '../contexts/TenantContext';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';

const EventDetails = () => {
  const { t } = useTranslation(); // Call useTranslation hook
  const theme = useTheme(); // Initialize theme
  const [event, setEvent] = useState(null);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const { tenant_id, event_id, task_id } = useParams(); // Extract tenant_id, event_id, and task_id from URL
  const navigate = useNavigate();
  const initialLoadPerformed = useRef(false); // Add this line
  const { selectedTenantId } = useTenant();
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      navigate(`/${tenant_id}/login`); // Redirect to tenant-specific login if not authenticated
      return;
    }

    const fetchEventDetails = async () => {
      try {
        setLoading(true);
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        const response = await axios.get(`${BACKEND_URL}/app/api/my-events/${event_id}/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        setEvent(response.data);
      } catch (error) {
        console.error('Error fetching event details', error);
        setError('Failed to load event details. Please try again later.');
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    if (!initialLoadPerformed.current) {
      fetchEventDetails();
      initialLoadPerformed.current = true;
    }
  }, [tenant_id, event_id, task_id, navigate]);

  // Déplace fetchComments hors du useEffect pour qu'il soit accessible partout
  const fetchComments = async () => {
    if (!selectedTenantId || !event_id) return;
    try {
      setLoadingComments(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await axios.get(`${BACKEND_URL}/app/api/t:${selectedTenantId}/event/${event_id}/comments`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      // Correction : support du format paginé { count, results }
      if (Array.isArray(response.data)) {
        setComments(response.data);
      } else if (response.data && Array.isArray(response.data.results)) {
        setComments(response.data.results);
      } else {
        setComments([]);
      }
    } catch (error) {
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  // Pour éviter le double appel, ne lance fetchComments qu'une seule fois au premier chargement
  const commentsLoaded = useRef(false);
  useEffect(() => {
    if (!commentsLoaded.current && selectedTenantId && event_id) {
      fetchComments();
      commentsLoaded.current = true;
    }
  }, [selectedTenantId, event_id]);

  const handleBack = () => {
    navigate(-1);
  };

  // Handle adaptive card actions
  const onActionSubmit = (actionData) => {
    console.log('Card action submitted:', actionData);
    if (actionData.data.action === 'join') {
      alert(t('eventDetails.joinedEvent', { title: event.title })); // Translate alert message
    } else if (actionData.data.action === 'cancel') {
      alert(t('eventDetails.registrationCancelled')); // Translate alert message
    } else if (actionData.data.action === 'contact') {
      alert(t('eventDetails.contactingOrganizer')); // Translate alert message
    }
  };

  // Ajoute une fonction pour rafraîchir les commentaires après ajout
  const refreshComments = () => {
    fetchComments();
  };

  if (loading) {
    return <div className="loading">{t('eventDetails.loading')}</div>; // Translate loading message
  }

  if (error) {
    return <div className="error">{error}</div>; // Error message is already dynamic
  }

  return (
    <div>
      {/* <Header title={t('eventDetails.pageTitle')} /> */}

      <div className="event-details-container">
        <button 
          onClick={handleBack} 
          className="back-button" 
          onMouseEnter={() => setIsButtonHovered(true)}
          onMouseLeave={() => setIsButtonHovered(false)}
          style={{
            color: isButtonHovered ? 'white' : theme.palette.primary.main,
            backgroundColor: isButtonHovered ? theme.palette.primary.main : 'transparent',
            borderColor: theme.palette.primary.main
          }}
        >
          {t('eventDetails.backToDashboard')}
        </button> {/* Translate back button */}

        {event ? (
          <>
            <div style={{ marginTop: 32, width: '100%', maxWidth: 700, marginLeft: 'auto', marginRight: 'auto' }}>
              <EventDetailsCard event={event} formatDate={date => date ? new Date(date).toLocaleString() : ''} onCommentAdded={refreshComments} />
            </div>
            {/* Liste des commentaires */}
            <div style={{ marginTop: 48, width: '100%', maxWidth: 700, marginLeft: 'auto', marginRight: 'auto' }}>
              <h3>{t('eventDetails.commentsTitle', 'Commentaires')}</h3>
              {loadingComments ? (
                <div>{t('eventDetails.loadingComments', 'Chargement des commentaires...')}</div>
              ) : comments.length === 0 ? (
                <div>{t('eventDetails.noComments', 'Aucun commentaire pour cet événement.')}</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {comments.map((comment, idx) => (
                    <Card key={comment.id || idx} sx={{ mb: 1, background: '#fff', borderRadius: 2, boxShadow: 1 }}>
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonOutlineOutlinedIcon sx={{ fontSize: 18, mr: 0.5 }} />
                          {comment.user_name || comment.user_email || 'Utilisateur'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                          {comment.created_dt ? new Date(comment.created_dt).toLocaleString() : ''}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1, textAlign: 'left', fontSize: '0.90rem' }}>
                          {comment.comment}
                        </Typography>
                        {comment.rating && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <span style={{ fontSize: '1.2rem' }}>🍌</span> {comment.rating}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <p>{t('eventDetails.eventNotFound')}</p> // Translate event not found message
        )}
      </div>
    </div>
  );
};

export default EventDetails;
